import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WifiOff,
  Sync,
  LocalHospital,
  Download,
  Add,
  Visibility,
  Edit,
  PregnantWoman,
  FirstPage,
  ChevronLeft,
  ChevronRight,
  LastPage,
  Search,
  CheckCircle,
  Event,
} from '@mui/icons-material';
import { getreferrals } from '../services/authService';
import { showSuccess, showError } from '../utils/alert';
import Loader from '../components/Loader';
import { useSelector } from 'react-redux';

const Referrals = () => {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const perPage = 10;
 const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  // Handle offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true); 

    window.addEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch referrals
  const fetchReferrals = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getreferrals(page, perPage, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery,
      });

      if (response.status === true) {
        const referralData = Array.isArray(response.data?.data) ? response.data.data : [];
        setReferrals(referralData);
        setCurrentPage(response.data?.meta?.current_page || 1);
        setTotalPages(response.data?.meta?.last_page || 1);
      } else {
        setReferrals([]);
        showError(response.message || 'No referrals found');
      }
    } catch (err) {
      console.error('Error fetching referrals:', err);
      setReferrals([]);
      setError('Failed to fetch referrals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch referrals on mount and when page or filters change
  useEffect(() => {
    fetchReferrals(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, searchQuery, selectedVillageId]);

  // Filter referrals locally for search and status
  useEffect(() => {
    let filtered = [...referrals];

    if (searchQuery.trim()) {
      filtered = filtered.filter((r) =>
        r.beneficiary_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredReferrals(filtered);
  }, [referrals, searchQuery]);

  const handleClick = () => {
    navigate('/referral-add');
  };

  const handleTabChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const getStatusIcon = (completed) => {
    return completed ? (
      <CheckCircle className="icon status-completed" />
    ) : (
      <Event className="icon status-pending" />
    );
  };

  if (loading) return <Loader />;

  return (
    <>
     
      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">Referrals</div>
        <div className="header-actions">
          <button className="button outline">
            <Download className="icon" />
            Export
          </button>
          <button className="add-button" onClick={handleClick}>
            <Add className="icon" />
            New Referral
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="quick-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#4CAF50' }}>
            <LocalHospital />
          </div>
          <div className="stat-info">
            <div className="stat-value">{referrals.length}</div>
            <div className="stat-label">Total Referrals</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#2196F3' }}>
            <Event />
          </div>
          <div className="stat-info">
            <div className="stat-value">
              {referrals.filter((r) => !r.completed).length}
            </div>
            <div className="stat-label">Active Referrals</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#4CAF50' }}>
            <CheckCircle />
          </div>
          <div className="stat-info">
            <div className="stat-value">
              {referrals.filter((r) => r.completed).length}
            </div>
            <div className="stat-label">Completed Referrals</div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="search-field">
          <Search className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by patient name..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Referrals List Card */}
      <div className="card">
        <div className="card-title">
          <div className="card-title-text">
            <LocalHospital className="icon" />
            Referral List
          </div>
        </div>
        <div className="card-subtitle">Manage and track patient referrals to health facilities</div>

        {/* Tabs */}
        <div className="tabs">
          <div
            className={`tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            All Referrals
            <span className="tab-count">{referrals.length}</span>
          </div>
          <div
            className={`tab ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => handleTabChange('active')}
          >
            Active
            <span className="tab-count">{referrals.filter((r) => !r.completed).length}</span>
          </div>
          <div
            className={`tab ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => handleTabChange('completed')}
          >
            Completed
            <span className="tab-count">{referrals.filter((r) => r.completed).length}</span>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Referred To</th>
                <th>Reason</th>
                <th>Urgency</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReferrals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    No referrals found.
                  </td>
                </tr>
              ) : (
                filteredReferrals.map((referral) => (
                  <tr key={referral.id}>
                    <td>
                      <div className="patient-info">
                        <div className="patient-avatar">
                          <PregnantWoman className="icon" />
                        </div>
                        <div>
                          <div className="patient-name">{referral.beneficiary_name || 'Unknown'}</div>
                          <div className="patient-details">
                            {referral.beneficiary_age || 'N/A'} yrs • {referral.beneficiary_gender || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{referral.referred_to || 'N/A'}</td>
                    <td>{referral.reason || 'N/A'}</td>
                    <td>
                      <span className={`badge badge-${referral.urgency_level?.toLowerCase() || 'unknown'}`}>
                        {referral.urgency_level || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      {referral.referral_datetime
                        ? new Date(referral.referral_datetime).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td>{getStatusIcon(referral.completed)}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-button">
                          <Visibility className="icon" />
                        </button>
                        <button className="action-button">
                          <Edit className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            <FirstPage className="icon" />
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="icon" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`pagination-button ${currentPage === i + 1 ? 'active' : ''}`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="icon" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            <LastPage className="icon" />
          </button>
          <div className="pagination-text">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      </div>
    </>
  );
};

export default Referrals; 